import { trackGA4Event, trackGA4EventAfterPageView } from '@gumtree/shared/src/util/ga4-shared';
import { v4 as uuidv4 } from 'uuid';

// DOC: https://docs.google.com/spreadsheets/d/1000000000000000000000000000000000000000/edit?gid=0#gid=0
interface AdLoadingMetrics {
  slotId: string;
  
  pageLoadStart: number;
  
  // js load
  gptScriptRequestStart?: number;
  gptLoadComplete?: number;

  adInitStart?: number;
  prebidStart?: number;
  prebidComplete?: number;
  consentStart?: number;
  consentComplete?: number;
  adRequestStart?: number;
  adRenderComplete?: number;
  adViewable?: number;
  
  isEmpty?: boolean;
  errorType?: string;
  // Cached GAM identifiers for click tracking per slot
  lineItemId?: string;
  creativeId?: string;
  isBackfill?: boolean;

  abandonFlushed?: boolean;
  pendingTimers?: number[];
  pendingBucketsReported?: Record<number, boolean>;
}

declare global {
  interface Window {
    gumtreeDataLayer: any[];
    clientData?: any;
  }
}

interface GoogletagWithPubads {
  cmd?: Array<() => void>;
  pubads?: () => {
    addEventListener: (event: string, callback: (event: any) => void) => void;
    setTargeting: (key: string, value: string) => void;
    refresh: (slots: any[]) => void;
    getSlots?: () => any[];
  };
}

class AdPerformanceTracker {
    private metrics: Map<string, AdLoadingMetrics> = new Map();
    private visitId: string;
    private pendingGlobalBucketsReported: Record<number, boolean> = {};
    private globalTimings: {
      gptScriptRequestStart?: number;
      gptLoadComplete?: number;
      prebidStart?: number;
      prebidComplete?: number;
      consentStart?: number;
      consentComplete?: number;
      adInitStart?: number;
    } = {};
    
    constructor() {
      this.initGlobalTimings();
      this.setupGPTEventListeners();
      this.setupUnloadFlush();
      this.visitId = uuidv4();
    }
    
    private initGlobalTimings() {
      if (typeof window !== 'undefined' && window.performance) {
        // No-op for now. Per-slot metrics will be created on-demand.
      }
    }
    
    markGPTScriptRequestStart() {
      this.globalTimings.gptScriptRequestStart = performance.now();
    }
    setGPTScriptRequestStartTime(timestamp: number) {
      this.globalTimings.gptScriptRequestStart = timestamp;
    }
    
    initGPTLoadTracking() {
      if (typeof window === 'undefined') return;
      
      const googletag = (window as any).googletag;
      
      if (googletag && googletag.pubads) {
        this.markGPTLoaded();
      } else {
        (window as any).googletag = (window as any).googletag || {};
        (window as any).googletag.cmd = (window as any).googletag.cmd || [];
        (window as any).googletag.cmd.push(() => {
          this.markGPTLoaded();
        });
      }
    }
    
    private markGPTLoaded() {
      this.globalTimings.gptLoadComplete = performance.now();
    }
    
    markAdInitStart(slotId?: string) {
      const timestamp = performance.now();
      
      if (slotId) {
        let metric = this.metrics.get(slotId);
        if (!metric) {
          metric = { slotId, pageLoadStart: 0 };
          this.metrics.set(slotId, metric);
        }
        metric.adInitStart = timestamp;
        this.applyGlobalTimingsToSlot(metric);

        // Schedule pending snapshots as early as possible for this slot
        this.schedulePendingSnapshots(slotId);
      } else {
        // record globally and apply to existing slots that haven't been set
        this.globalTimings.adInitStart = timestamp;
        this.metrics.forEach((m) => {
          if (!m.adInitStart) {
            m.adInitStart = timestamp;
            this.applyGlobalTimingsToSlot(m);
          }
        });
      }
    }

    trackAdRequestStart(slotIds?: string) {
      const trackDataBI = {
        event: 'gam_ads_request',
        lineItemId: 'GAM_ADVERT',
        creativeId: '',
        slot_name: slotIds,
        visit_id: this.visitId,
      }
      trackGA4EventAfterPageView(trackDataBI);
    }

    
    private applyGlobalTimingsToSlot(metric: AdLoadingMetrics) {
      if (this.globalTimings.gptScriptRequestStart && !metric.gptScriptRequestStart) {
        metric.gptScriptRequestStart = this.globalTimings.gptScriptRequestStart;
      }
      if (this.globalTimings.gptLoadComplete && !metric.gptLoadComplete) {
        metric.gptLoadComplete = this.globalTimings.gptLoadComplete;
      }
    }
    
    markPrebidStart() {
      this.globalTimings.prebidStart = performance.now();
    }
    
    markPrebidComplete() {
      this.globalTimings.prebidComplete = performance.now();
    }
    
    markConsentStart() {
      this.globalTimings.consentStart = performance.now();
    }
    
    markConsentComplete() {
      this.globalTimings.consentComplete = performance.now();
    }
    
    private markAdRequestStart(slotId: string) {
      let metric = this.metrics.get(slotId);
      if (!metric) {
        metric = { slotId, pageLoadStart: 0 };
        this.metrics.set(slotId, metric);
      }
      metric.adRequestStart = performance.now();
      
      if (this.globalTimings.prebidStart && !metric.prebidStart) {
        metric.prebidStart = this.globalTimings.prebidStart;
      }
      if (this.globalTimings.prebidComplete && !metric.prebidComplete) {
        metric.prebidComplete = this.globalTimings.prebidComplete;
      }
      if (this.globalTimings.consentStart && !metric.consentStart) {
        metric.consentStart = this.globalTimings.consentStart;
      }
      if (this.globalTimings.consentComplete && !metric.consentComplete) {
        metric.consentComplete = this.globalTimings.consentComplete;
      }
      if (this.globalTimings.adInitStart && !metric.adInitStart) {
        metric.adInitStart = this.globalTimings.adInitStart;
      }

      // Schedule pending snapshots at thresholds
      this.schedulePendingSnapshots(slotId);
    }
    
    private setupGPTEventListeners() {
      if (typeof window === 'undefined') return;
      
      const googletag = (window as any).googletag as GoogletagWithPubads;
      if (!googletag) return;
      
      googletag.cmd = googletag.cmd || [];
      
      googletag.cmd.push(() => {
        if (!googletag?.pubads) return;
        
        const pubads = googletag.pubads();
        
        pubads.addEventListener('slotRequested', (event: any) => {
          const slotId = event.slot.getSlotElementId();
          this.markAdRequestStart(slotId);
        });
        
        pubads.addEventListener('slotRenderEnded', (event: any) => {
          // Cache identifiers per-slot for later click tracking
          const slotId = event.slot.getSlotElementId();
          const existingMetric = this.metrics.get(slotId);
          // Always track all slots
          const resp = event.slot.getResponseInformation?.() as any | undefined;

          const lineItemId = event.lineItemId ?? resp?.lineItemId ?? resp?.sourceAgnosticLineItemId ?? '';
          const creativeId = event.creativeId ?? resp?.creativeId ?? resp?.sourceAgnosticCreativeId ?? '';
          const isBackfill = resp?.isBackfill ?? event.isBackfill ?? false;

          // console.log('slotRenderEnded===!!!', event, resp);

          if (existingMetric) {
            existingMetric.lineItemId = lineItemId;
            existingMetric.creativeId = creativeId;
            existingMetric.isBackfill = isBackfill;
          } else {
            this.metrics.set(slotId, {
              slotId,
              pageLoadStart: 0,
              lineItemId: lineItemId,
              creativeId: creativeId,
              isBackfill: isBackfill,
            });
          }
          
          let metric = this.metrics.get(slotId);
          if (!metric) {
            metric = { slotId, pageLoadStart: 0 };
            this.metrics.set(slotId, metric);
          }
          metric.adRenderComplete = performance.now();
          metric.isEmpty = event.isEmpty;

          // Clear pending timers on completion
          this.clearPendingTimers(metric);

          const trackCalcData = {
            totalTime: metric.adRenderComplete && metric.pageLoadStart !== undefined
              ? metric.adRenderComplete - metric.pageLoadStart : 0,
            adLoadTime: metric.adRenderComplete && metric.adRequestStart
              ? metric.adRenderComplete - metric.adRequestStart : 0,
            initTime: metric.adRequestStart && metric.adInitStart
              ? metric.adRequestStart - metric.adInitStart : 0,
            prebidTime: metric.prebidComplete && metric.prebidStart
              ? metric.prebidComplete - metric.prebidStart : 0,
            consentTime: metric.consentComplete && metric.consentStart
              ? metric.consentComplete - metric.consentStart : 0,
            // js load time
            gptTotalTime: metric.gptLoadComplete && metric.pageLoadStart !== undefined
              ? metric.gptLoadComplete - metric.pageLoadStart : 0,
            jsTagLoadTime: metric.gptScriptRequestStart && metric.pageLoadStart !== undefined
              ? metric.gptScriptRequestStart - metric.pageLoadStart : 0,
            jsTagLoad2entryInit: metric.gptScriptRequestStart && metric.gptLoadComplete !== undefined
              ? metric.gptLoadComplete - metric.gptScriptRequestStart : 0,
          }
          
          this.reportToGA4(metric, trackCalcData);
          this.reportAdRes({
            slotId,
            isEmpty: event.isEmpty,
            creativeId,
            lineItemId,
            isBackfill,
          }, trackCalcData);
        });
        
        pubads.addEventListener('impressionViewable', (event: any) => {
          const slotId = event.slot.getSlotElementId();
          
          let metric = this.metrics.get(slotId);
          if (!metric) {
            metric = { slotId, pageLoadStart: 0 };
            this.metrics.set(slotId, metric);
          }
          metric.adViewable = performance.now();
          this.updateGA4Metrics(slotId, { adViewable: metric.adViewable });

          // Clear pending timers when viewable
          this.clearPendingTimers(metric);
        });
      });
    }
    
    private setupUnloadFlush() {
      if (typeof window === 'undefined' || typeof document === 'undefined') return;
      const flush = (trigger: 'pagehide' | 'visibilitychange') => this.flushAbandonEvents(trigger);
      // pagehide covers bfcache; visibilitychange(hidden) is a safety net
      try {
        window.addEventListener('pagehide', () => flush('pagehide'), { capture: true } as any);
      } catch (_e) {
        // older browsers without options
        window.addEventListener('pagehide', () => flush('pagehide'));
      }
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush('visibilitychange');
      }, { capture: true } as any);
    }
    
    private flushAbandonEvents(trigger: 'pagehide' | 'visibilitychange') {
      if (typeof window === 'undefined') return;

      const now = performance.now();
      const pending = Array.from(this.metrics.values()).filter((m) => !m.adRenderComplete);

      if (pending.length === 0) return;

      for (const m of pending) {
        if (m.adRenderComplete) return; // If the slot already completed, no need to schedule
        if (m.abandonFlushed) return; // If the slot already abandoned, no need to schedule
        m.abandonFlushed = true;

        const params: any = {
          slot_name: m.slotId,
          lineItemId: 'GAM_ADVERT',
          creativeId: '',
          loading_time: Math.round(now),
          loading_stage: m.adRequestStart ? 'after_request' : 'before_request',
          event_trigger: trigger,
          transport_type: 'beacon',
          visit_id: this.visitId,
        };
        this.sendGa4Beacon('gam_ads_loading', params);
      }
    }

    private schedulePendingSnapshots(slotId: string) {
      const metric = this.metrics.get(slotId);
      if (!metric) return;
      // If the slot already completed, no need to schedule
      if (metric.adRenderComplete) return;
      // Avoid duplicate scheduling
      if (metric.pendingTimers && metric.pendingTimers.length > 0) return;
      const thresholds = [2000, 5000, 10000, 15000, 20000, 30000];
      if (!metric.pendingTimers) metric.pendingTimers = [];
      if (!metric.pendingBucketsReported) metric.pendingBucketsReported = {};

      const adRequestStart = metric.adRequestStart || 0;
      const adInitStart = metric.adInitStart || this.globalTimings.adInitStart || 0;
      const startBase = Math.min(adRequestStart, adInitStart);
      // console.log('startBase===', slotId, startBase);

      const now = performance.now();
      for (const t of thresholds) {
        const delay = Math.max(0, startBase + t - now);
        const timerId = window.setTimeout(() => {
          if (this.pendingGlobalBucketsReported[t]) return;

          const pendingSlots = Array.from(this.metrics.values()).filter((mm) => !mm.adRenderComplete);
          if (pendingSlots.length === 0) return;

          const slotNames = pendingSlots.map((mm) => mm.slotId).join();
          const params: any = {
            event: 'gam_ads_loading',
            slot_name: slotNames,
            lineItemId: 'GAM_ADVERT',
            creativeId: '',
            loading_time: Math.round(performance.now() - startBase),
            pending_bucket_ms: t,
            event_trigger: 'polling',
            visit_id: this.visitId,
          };
          trackGA4EventAfterPageView(params);
          this.pendingGlobalBucketsReported[t] = true;
        }, delay);
        metric.pendingTimers.push(timerId as unknown as number);
      }
    }

    private clearPendingTimers(metric: AdLoadingMetrics) {
      if (metric.pendingTimers && metric.pendingTimers.length) {
        for (const id of metric.pendingTimers) {
          try { clearTimeout(id as unknown as number); } catch (_e) {}
        }
        metric.pendingTimers = [];
      }
    }
    
    private sendGa4Beacon(eventName: string, params: Record<string, unknown>) {
      try {
        const gtag = (window as any).gtag as undefined | ((...args: any[]) => void);
        if (typeof gtag === 'function') {
          gtag('event', eventName, params);
          return;
        }
      } catch (_e) {
        // fall through to dataLayer
      }
      try {
        // fallback to dataLayer push; may drop on unload in some browsers
        (window as any).gumtreeDataLayer = (window as any).gumtreeDataLayer || [];
        (window as any).gumtreeDataLayer.push({ event: eventName, ...params });
      } catch (_e) {
        // swallow
      }
    }
    
    private reportToGA4(metrics: AdLoadingMetrics, trackCalcData) {
      // console.log('ad_loading_performance===', metrics);
      if (typeof window === 'undefined') return;
      const targetSlots = ['tBanner', 'vipBanner'];
      if (!targetSlots.includes(metrics.slotId)) {
          return;
      }
      
      const performanceTrackData = {
        event: 'ad_loading_performance',
        slot_id: metrics.slotId,
        total_time: Math.round(trackCalcData.totalTime),
        ad_load_time: Math.round(trackCalcData.adLoadTime),
        init_time: Math.round(trackCalcData.initTime),
        prebid_time: Math.round(trackCalcData.prebidTime),
        consent_time: Math.round(trackCalcData.consentTime),
        
        // GPT js
        gpt_load_time: Math.round(trackCalcData.gptTotalTime),
        js_tag_load_time: Math.round(trackCalcData.jsTagLoadTime),
        js_tag_load2entry_init: Math.round(trackCalcData.jsTagLoad2entryInit),
        
        // load result
        is_empty: metrics.isEmpty || false,
        visit_id: this.visitId,
      }
      // console.log('metrics===', metrics)
      // console.log('gptTotalTime===', performanceTrackData)
      trackGA4EventAfterPageView(performanceTrackData);
    }

    private reportAdRes(metrics, trackCalcData) {
      // console.log('reportAdRes===', metrics.slotId, metrics);
      if (typeof window === 'undefined') return;
      const trackDataBI = {
        event: metrics.isEmpty ? 'gam_ads_view_error' : 'gam_ads_view_success',
        lineItemId: metrics.lineItemId || '',
        creativeId: metrics.creativeId,
        isBackfill: metrics.isBackfill,
        slot_name: metrics.slotId,

        loading_time: Math.round(trackCalcData.totalTime), // Consistent with App: use 'loading_time'
        ad_load_time: Math.round(trackCalcData.adLoadTime),
        init_time: Math.round(trackCalcData.initTime),
        prebid_time: Math.round(trackCalcData.prebidTime),
        consent_time: Math.round(trackCalcData.consentTime),
        
        // GPT js
        gpt_load_time: Math.round(trackCalcData.gptTotalTime),
        js_tag_load_time: Math.round(trackCalcData.jsTagLoadTime),
        js_tag_load2entry_init: Math.round(trackCalcData.jsTagLoad2entryInit),
        visit_id: this.visitId,
      }
      trackGA4EventAfterPageView(trackDataBI);
    }
    
    private updateGA4Metrics(slotId: string, updates: Partial<AdLoadingMetrics>) {
      const metric = this.metrics.get(slotId);
      if (metric && updates.adViewable) {
        // unnecessary ad_viewable
      }
    }
    
    getMetrics(slotId?: string) {
      if (slotId) {
        return this.metrics.get(slotId);
      }
      return Object.fromEntries(this.metrics);
    }
    
    public trackAdClick(slotId: string) {
      const metrics = this.metrics.get(slotId);
      
      if (!metrics) return;
  
      const payload = {
        event: 'gam_ads_click',
        slot_name: slotId,
        lineItemId: metrics.lineItemId || '',
        creativeId: metrics.creativeId,
        isBackfill: metrics.isBackfill,
        visit_id: this.visitId,
      } as const;
  
      trackGA4Event(payload);
    }
  }

  
  export const adPerformanceTracker = new AdPerformanceTracker();
